import { getCurrentDemoClientId } from 'tio-employee/utils/career-pathways-demo-data';
import type {
  AssessmentQuestion,
  CareerCapitalScore,
  CareerFamily,
  CareerPlan,
  CareerRole,
  CareerSummary,
  EmployeeCareerProfile,
  FinancialSnapshot,
  LrapDiscoverySummary,
  LrapProgram,
  PslfAchievement,
  PslfAutomationStatus,
  PslfJourney,
  PslfPaymentValidation,
  PslfProgress,
  PslfProjection,
  PslfProtectionStatus,
  PslfScenario,
  PslfVaultDocument,
  PslfVaultSummary,
  Recommendation,
  ScoreHistoryPoint,
  ScoreLevel,
  Section127Recommendation,
  SkillGap,
  StackingOptimization,
  SuccessStory,
} from './types';

export function getEmployeeProfileForClient() {
  const base = {
    ...MOCK_EMPLOYEE,
    currentRole: { ...MOCK_EMPLOYEE.currentRole },
    targetRole: MOCK_EMPLOYEE.targetRole ? { ...MOCK_EMPLOYEE.targetRole } : null,
    taBenefit: { ...MOCK_EMPLOYEE.taBenefit },
    skills: [...(MOCK_EMPLOYEE.skills ?? [])],
  };

  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return {
      ...base,
      firstName: 'Carlos',
      lastName: 'Johnson',
      email: 'carlos.johnson@ups.com',
      currentRole: {
        title: 'Package Handler',
        department: 'Operations',
        level: 'Entry',
        startDate: '2022-09-12',
        yearsInRole: 2,
      },
      targetRole: {
        id: 'hub-supervisor',
        title: 'Hub Supervisor',
        targetDate: '2026-06-01',
        matchScore: 62,
      },
      taBenefit: {
        annualMax: 3500,
        used: 900,
        remaining: 2600,
        eligiblePrograms: 12,
      },
      careerPlanProgress: 42,
    };
  }

  if (clientId === 'starbucks') {
    return {
      ...base,
      firstName: 'Alex',
      lastName: 'Kim',
      email: 'alex.kim@starbucks.com',
      currentRole: {
        title: 'Barista',
        department: 'Store Operations',
        level: 'Entry',
        startDate: '2023-05-10',
        yearsInRole: 2,
      },
      targetRole: {
        id: 'store-manager',
        title: 'Store Manager',
        targetDate: '2027-01-15',
        matchScore: 58,
      },
      taBenefit: {
        annualMax: 5250,
        used: 2400,
        remaining: 2850,
        eligiblePrograms: 18,
      },
      careerPlanProgress: 35,
    };
  }

  return base;
}

export function getSummaryForClient() {
  const base = { ...MOCK_SUMMARY };
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return {
      ...base,
      totalGaps: 6,
      criticalGaps: 2,
      totalLearningOptions: 38,
      taEligibleOptions: 14,
      maxTaCoverage: '$3,500',
      skillsTracked: 14,
      skillsVerified: 6,
      skillsInProgress: 4,
      skillsMastered: 4,
      coursesInProgress: 2,
      profileCompleteness: 62,
    };
  }

  if (clientId === 'starbucks') {
    return {
      ...base,
      totalGaps: 8,
      criticalGaps: 3,
      totalLearningOptions: 46,
      taEligibleOptions: 20,
      maxTaCoverage: '$5,250',
      skillsTracked: 16,
      skillsVerified: 7,
      skillsInProgress: 5,
      skillsMastered: 4,
      coursesInProgress: 3,
      profileCompleteness: 68,
    };
  }

  return base;
}

export function getCareerPlanForClient() {
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return UPS_CAREER_PLAN;
  }

  if (clientId === 'starbucks') {
    return STARBUCKS_CAREER_PLAN;
  }

  return MOCK_CAREER_PLAN;
}

export function getRecommendationsForClient() {
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return UPS_RECOMMENDATIONS;
  }

  if (clientId === 'starbucks') {
    return STARBUCKS_RECOMMENDATIONS;
  }

  return MOCK_RECOMMENDATIONS;
}

export function getCareerFamiliesForClient() {
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return UPS_CAREER_FAMILIES;
  }

  if (clientId === 'starbucks') {
    return STARBUCKS_CAREER_FAMILIES;
  }

  return MOCK_CAREER_FAMILIES;
}

export function getCareerRolesForClient() {
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return UPS_CAREER_ROLES;
  }

  if (clientId === 'starbucks') {
    return STARBUCKS_CAREER_ROLES;
  }

  return MOCK_CAREER_ROLES;
}

export function getFeaturedRolesForClient() {
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return UPS_CAREER_ROLES.slice(0, 3);
  }

  if (clientId === 'starbucks') {
    return STARBUCKS_CAREER_ROLES.slice(0, 3);
  }

  return MOCK_FEATURED_ROLES;
}

export function getSuccessStoriesForClient() {
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return UPS_SUCCESS_STORIES;
  }

  if (clientId === 'starbucks') {
    return STARBUCKS_SUCCESS_STORIES;
  }

  return MOCK_SUCCESS_STORIES;
}

export function getSkillGapsForClient() {
  const clientId = getCurrentDemoClientId();

  if (clientId === 'ups') {
    return UPS_SKILL_GAPS;
  }

  if (clientId === 'starbucks') {
    return STARBUCKS_SKILL_GAPS;
  }

  return MOCK_SKILL_GAPS;
}

export function getFinancialSnapshotForClient(): FinancialSnapshot {
  const clientId = getCurrentDemoClientId();

  const baseSnapshot: FinancialSnapshot = {
    section127: {
      annualLimit: 5250,
      yearToDateUsed: 1850,
      remaining: 3400,
      loanAllocation: 1200,
      tuitionAllocation: 650,
      strategy: 'balanced',
      updatedAt: '2025-01-15T10:30:00Z',
    },
    studentLoans: {
      totalBalance: 42500,
      monthlyPayment: 385,
      interestRate: 6.5,
      estimatedPayoffDate: '2034-08-15',
      servicer: 'MOHELA',
      paymentsMade: 36,
    },
    employerContributions: {
      yearToDate: 1850,
      lifetime: 8750,
      projectedAnnual: 5250,
    },
    optimizationPotential: {
      potentialSavings: 2400,
      projectedPayoffAcceleration: '2.5 years',
      taxSavingsYtd: 462.5,
    },
  };

  if (clientId === 'ups') {
    return {
      ...baseSnapshot,
      section127: {
        ...baseSnapshot.section127,
        annualLimit: 3500,
        yearToDateUsed: 900,
        remaining: 2600,
        loanAllocation: 600,
        tuitionAllocation: 300,
      },
      studentLoans: {
        totalBalance: 28000,
        monthlyPayment: 285,
        interestRate: 5.8,
        estimatedPayoffDate: '2032-03-15',
        servicer: 'Nelnet',
        paymentsMade: 48,
      },
      employerContributions: {
        yearToDate: 900,
        lifetime: 5600,
        projectedAnnual: 3500,
      },
      optimizationPotential: {
        potentialSavings: 1800,
        projectedPayoffAcceleration: '1.8 years',
        taxSavingsYtd: 225.0,
      },
    };
  }

  if (clientId === 'starbucks') {
    return {
      ...baseSnapshot,
      section127: {
        ...baseSnapshot.section127,
        yearToDateUsed: 2400,
        remaining: 2850,
        loanAllocation: 1500,
        tuitionAllocation: 900,
        strategy: 'loan-focused',
      },
      studentLoans: {
        totalBalance: 35000,
        monthlyPayment: 320,
        interestRate: 6.2,
        estimatedPayoffDate: '2033-06-01',
        servicer: 'FedLoan',
        paymentsMade: 24,
      },
      employerContributions: {
        yearToDate: 2400,
        lifetime: 7200,
        projectedAnnual: 5250,
      },
      optimizationPotential: {
        potentialSavings: 3200,
        projectedPayoffAcceleration: '2.8 years',
        taxSavingsYtd: 600.0,
      },
    };
  }

  return baseSnapshot;
}

export function getSection127RecommendationForClient(): Section127Recommendation {
  const clientId = getCurrentDemoClientId();
  const snapshot = getFinancialSnapshotForClient();

  const baseRecommendation: Section127Recommendation = {
    recommendedStrategy: 'balanced',
    recommendedLoanAllocation: 2500,
    recommendedTuitionAllocation: 2750,
    reasoning: `Based on your loan balance of $${snapshot.studentLoans.totalBalance.toLocaleString()} at ${snapshot.studentLoans.interestRate}% interest, a balanced approach maximizes both career growth and debt reduction.`,
    factors: [
      {
        name: 'Interest Rate',
        impact: 'high',
        description: `Your ${snapshot.studentLoans.interestRate}% rate is above the optimal threshold for prioritizing tuition.`,
      },
      {
        name: 'Career Stage',
        impact: 'medium',
        description: 'Early-to-mid career employees benefit from continued education investment.',
      },
      {
        name: 'Loan Balance',
        impact: 'high',
        description: `With $${snapshot.studentLoans.totalBalance.toLocaleString()} remaining, accelerating payoff provides significant long-term savings.`,
      },
    ],
    projectedImpact: {
      scenario: 'recommended',
      loanPayoffDate: '2032-01-15',
      totalInterestSaved: 8500,
      taxSavings: 1312.5,
      careerAdvancementScore: 85,
    },
    alternativeScenarios: [
      {
        scenario: 'loan-focused',
        loanPayoffDate: '2030-06-01',
        totalInterestSaved: 12800,
        taxSavings: 1312.5,
        careerAdvancementScore: 65,
      },
      {
        scenario: 'tuition-focused',
        loanPayoffDate: '2034-08-15',
        totalInterestSaved: 2100,
        taxSavings: 1312.5,
        careerAdvancementScore: 95,
      },
    ],
    actions: [
      {
        id: 'action-1',
        title: 'Adjust allocation to balanced strategy',
        description: 'Split your Section 127 benefit equally between loan payments and tuition.',
        impact: 'Saves $8,500 in interest while maintaining career growth',
        priority: 'high',
      },
      {
        id: 'action-2',
        title: 'Enroll in recommended program',
        description:
          'Start the Healthcare Leadership Certificate to boost career advancement score.',
        impact: 'Increases promotion readiness by 15%',
        priority: 'medium',
      },
    ],
    lastUpdated: new Date().toISOString(),
  };

  if (clientId === 'ups') {
    return {
      ...baseRecommendation,
      recommendedStrategy: 'loan-focused',
      recommendedLoanAllocation: 2500,
      recommendedTuitionAllocation: 1000,
      reasoning:
        'With your moderate loan balance and lower interest rate, focusing on debt reduction while maintaining some education investment is optimal.',
    };
  }

  if (clientId === 'starbucks') {
    return {
      ...baseRecommendation,
      recommendedStrategy: 'tuition-focused',
      recommendedLoanAllocation: 1500,
      recommendedTuitionAllocation: 3750,
      reasoning:
        'Your ASU partnership provides exceptional tuition coverage. Maximizing education investment accelerates your path to Store Manager.',
    };
  }

  return baseRecommendation;
}

export function getCareerCapitalScoreForClient(): CareerCapitalScore {
  const clientId = getCurrentDemoClientId();

  const baseScore: CareerCapitalScore = {
    totalScore: 685,
    level: 'silver' as ScoreLevel,
    percentile: 72,
    lastUpdated: new Date().toISOString(),
    components: {
      skillVelocity: {
        score: 78,
        weight: 25,
        trend: 'up',
        factors: [
          { name: 'Skills acquired this quarter', value: 3, benchmark: 2 },
          { name: 'Certifications earned', value: 1, benchmark: 1 },
          { name: 'Learning hours completed', value: 45, benchmark: 40 },
        ],
      },
      careerReadiness: {
        score: 68,
        weight: 30,
        trend: 'stable',
        factors: [
          { name: 'Target role match score', value: 68, benchmark: 75 },
          { name: 'Skills gap closure rate', value: 65, benchmark: 70 },
          { name: 'Promotion readiness', value: 70, benchmark: 75 },
        ],
      },
      financialOptimization: {
        score: 82,
        weight: 20,
        trend: 'up',
        factors: [
          { name: 'Section 127 utilization', value: 85, benchmark: 80 },
          { name: 'Loan payoff acceleration', value: 78, benchmark: 70 },
          { name: 'Tax savings captured', value: 82, benchmark: 75 },
        ],
      },
      learningProgress: {
        score: 72,
        weight: 15,
        trend: 'up',
        factors: [
          { name: 'Course completion rate', value: 88, benchmark: 85 },
          { name: 'Assessment scores', value: 78, benchmark: 75 },
          { name: 'Learning streak (days)', value: 14, benchmark: 7 },
        ],
      },
      networkDensity: {
        score: 58,
        weight: 10,
        trend: 'stable',
        factors: [
          { name: 'Mentor connections', value: 1, benchmark: 2 },
          { name: 'Peer collaborations', value: 3, benchmark: 5 },
          { name: 'Industry connections', value: 12, benchmark: 20 },
        ],
      },
    },
    leaderboard: {
      rank: 156,
      totalParticipants: 520,
      teamRank: 8,
      teamSize: 24,
      departmentRank: 34,
      departmentSize: 180,
    },
    badges: [
      {
        id: 'badge-learner',
        name: 'Fast Learner',
        description: 'Completed 3 courses in one month',
        icon: 'trophy',
        earnedDate: '2025-01-10',
        category: 'learning',
      },
      {
        id: 'badge-saver',
        name: 'Tax Optimizer',
        description: 'Maximized Section 127 tax savings',
        icon: 'piggy-bank',
        earnedDate: '2024-12-15',
        category: 'financial',
      },
    ],
    nextMilestone: {
      targetScore: 750,
      level: 'gold',
      pointsNeeded: 65,
      estimatedDate: '2025-04-15',
    },
  };

  if (clientId === 'ups') {
    return {
      ...baseScore,
      totalScore: 542,
      level: 'bronze',
      percentile: 58,
      components: {
        ...baseScore.components,
        skillVelocity: {
          ...baseScore.components.skillVelocity,
          score: 62,
          trend: 'up',
        },
        careerReadiness: {
          ...baseScore.components.careerReadiness,
          score: 52,
          trend: 'up',
        },
        financialOptimization: {
          ...baseScore.components.financialOptimization,
          score: 68,
          trend: 'stable',
        },
      },
      leaderboard: {
        rank: 892,
        totalParticipants: 2400,
        teamRank: 12,
        teamSize: 35,
        departmentRank: 145,
        departmentSize: 420,
      },
      nextMilestone: {
        targetScore: 600,
        level: 'silver',
        pointsNeeded: 58,
        estimatedDate: '2025-05-01',
      },
    };
  }

  if (clientId === 'starbucks') {
    return {
      ...baseScore,
      totalScore: 612,
      level: 'silver',
      percentile: 65,
      components: {
        ...baseScore.components,
        skillVelocity: {
          ...baseScore.components.skillVelocity,
          score: 70,
          trend: 'up',
        },
        careerReadiness: {
          ...baseScore.components.careerReadiness,
          score: 58,
          trend: 'up',
        },
        learningProgress: {
          ...baseScore.components.learningProgress,
          score: 82,
          trend: 'up',
        },
      },
      leaderboard: {
        rank: 1245,
        totalParticipants: 4200,
        teamRank: 5,
        teamSize: 18,
        departmentRank: 89,
        departmentSize: 350,
      },
      badges: [
        ...baseScore.badges,
        {
          id: 'badge-asu',
          name: 'ASU Scholar',
          description: 'Completed first ASU course',
          icon: 'graduation-cap',
          earnedDate: '2024-11-20',
          category: 'learning',
        },
      ],
      nextMilestone: {
        targetScore: 750,
        level: 'gold',
        pointsNeeded: 138,
        estimatedDate: '2025-06-01',
      },
    };
  }

  return baseScore;
}

export function getScoreHistoryForClient(months: number): ScoreHistoryPoint[] {
  const clientId = getCurrentDemoClientId();
  const baseScore = clientId === 'ups' ? 480 : clientId === 'starbucks' ? 520 : 580;
  const growthRate = clientId === 'ups' ? 5 : clientId === 'starbucks' ? 8 : 9;

  const history: ScoreHistoryPoint[] = [];
  const now = new Date();

  for (let i = months; i >= 0; i--) {
    const date = new Date(now);
    date.setMonth(date.getMonth() - i);

    const variance = Math.floor(Math.random() * 10) - 5;
    const score = Math.min(1000, baseScore + (months - i) * growthRate + variance);

    history.push({
      date: date.toISOString().split('T')[0] as string,
      score,
      level: score >= 750 ? 'gold' : score >= 500 ? 'silver' : 'bronze',
    });
  }

  return history;
}

export function getLeaderboardForClient(groupType: 'team' | 'department' | 'company') {
  const clientId = getCurrentDemoClientId();
  const currentScore = getCareerCapitalScoreForClient();

  const teamNames: Record<string, string[]> = {
    advocate: [
      'Sarah M.',
      'John D.',
      'Maria R.',
      'David K.',
      'Lisa T.',
      'Mike B.',
      'Emma W.',
      'Chris L.',
    ],
    ups: [
      'Carlos J.',
      'Devon R.',
      'Keisha M.',
      'Marcus T.',
      'Ashley B.',
      'Jordan P.',
      'Tyler S.',
      'Brianna W.',
    ],
    starbucks: [
      'Alex K.',
      'Jordan M.',
      'Taylor S.',
      'Casey R.',
      'Morgan L.',
      'Riley T.',
      'Sam D.',
      'Quinn B.',
    ],
  };

  const names = teamNames[clientId] ?? teamNames['advocate'] ?? [];
  const baseRank =
    groupType === 'team'
      ? currentScore.leaderboard.teamRank
      : groupType === 'department'
        ? currentScore.leaderboard.departmentRank
        : currentScore.leaderboard.rank;
  const totalSize =
    groupType === 'team'
      ? currentScore.leaderboard.teamSize
      : groupType === 'department'
        ? currentScore.leaderboard.departmentSize
        : currentScore.leaderboard.totalParticipants;

  const entries = names.slice(0, 8).map((name, index) => ({
    rank: index + 1,
    name: index === baseRank - 1 ? 'You' : name,
    score: currentScore.totalScore + (5 - index) * 25 + Math.floor(Math.random() * 20),
    change: Math.floor(Math.random() * 10) - 3,
    isCurrentUser: index === baseRank - 1,
  }));

  // Ensure current user is in the list
  const hasCurrentUser = entries.some((e) => e.isCurrentUser);
  if (!hasCurrentUser && entries.length > 0) {
    entries[Math.min(baseRank - 1, entries.length - 1)] = {
      rank: baseRank,
      name: 'You',
      score: currentScore.totalScore,
      change: 5,
      isCurrentUser: true,
    };
  }

  return {
    entries: entries.sort((a, b) => b.score - a.score).map((e, i) => ({ ...e, rank: i + 1 })),
    currentUserRank: {
      rank: baseRank,
      totalParticipants: totalSize,
      teamRank: currentScore.leaderboard.teamRank,
      teamSize: currentScore.leaderboard.teamSize,
      departmentRank: currentScore.leaderboard.departmentRank,
      departmentSize: currentScore.leaderboard.departmentSize,
    },
  };
}

const MOCK_EMPLOYEE: EmployeeCareerProfile = {
  id: 'emp-001',
  firstName: 'Maria',
  lastName: 'Rodriguez',
  email: 'maria.rodriguez@memorial.health',
  currentRole: {
    title: 'Registered Nurse (RN)',
    department: 'Medical-Surgical Unit',
    level: 'Senior',
    startDate: '2021-03-15',
    yearsInRole: 4,
  },
  targetRole: {
    id: 'nurse-manager',
    title: 'Nurse Manager',
    targetDate: '2026-12-01',
    matchScore: 68,
  },
  taBenefit: {
    annualMax: 5250,
    used: 1850,
    remaining: 3400,
    eligiblePrograms: 24,
  },
  careerPlanProgress: 68,
};

MOCK_EMPLOYEE.skills = [
  {
    id: 'skill-rn',
    name: 'RN License',
    category: 'certification',
    level: 'expert',
    proficiencyScore: 100,
    source: 'verified',
    verifiedDate: '2021-03-15',
    verifiedBy: 'State Board of Nursing',
  },
  {
    id: 'skill-bsn',
    name: 'BSN Degree',
    category: 'certification',
    level: 'expert',
    proficiencyScore: 100,
    source: 'verified',
    verifiedDate: '2024-08-15',
    verifiedBy: 'University Partners Network',
  },
  {
    id: 'skill-epic',
    name: 'Epic EMR',
    category: 'technical',
    level: 'advanced',
    proficiencyScore: 85,
    source: 'verified',
    verifiedDate: '2024-06-20',
    verifiedBy: 'Internal Certification',
  },
  {
    id: 'skill-leadership',
    name: 'Leadership',
    category: 'soft',
    level: 'intermediate',
    proficiencyScore: 65,
    source: 'in-progress',
    progress: 65,
    targetDate: '2025-02-15',
  },
  {
    id: 'skill-epic-super',
    name: 'Epic Super User',
    category: 'technical',
    level: 'intermediate',
    proficiencyScore: 40,
    source: 'in-progress',
    progress: 40,
    targetDate: '2025-03-01',
  },
  {
    id: 'skill-communication',
    name: 'Patient Communication',
    category: 'soft',
    level: 'expert',
    proficiencyScore: 95,
    source: 'self-reported',
  },
  {
    id: 'skill-critical',
    name: 'Critical Thinking',
    category: 'soft',
    level: 'advanced',
    proficiencyScore: 80,
    source: 'self-reported',
  },
];

const MOCK_CAREER_FAMILIES: CareerFamily[] = [
  {
    id: 'nursing',
    name: 'Nursing',
    icon: 'first-aid',
    rolesCount: 85,
    growth: '+18%',
    color: 'red',
  },
  {
    id: 'clinical-support',
    name: 'Clinical Support',
    icon: 'flask',
    rolesCount: 62,
    growth: '+15%',
    color: 'teal',
  },
  {
    id: 'health-informatics',
    name: 'Health Informatics',
    icon: 'code',
    rolesCount: 38,
    growth: '+28%',
    color: 'violet',
  },
  {
    id: 'administration',
    name: 'Healthcare Administration',
    icon: 'calculator',
    rolesCount: 45,
    growth: '+12%',
    color: 'blue',
  },
  {
    id: 'allied-health',
    name: 'Allied Health',
    icon: 'paint-brush',
    rolesCount: 52,
    growth: '+14%',
    color: 'pink',
  },
  {
    id: 'education-training',
    name: 'Education & Training',
    icon: 'book-open',
    rolesCount: 24,
    growth: '+10%',
    color: 'amber',
  },
];

const UPS_CAREER_FAMILIES: CareerFamily[] = [
  {
    id: 'operations',
    name: 'Operations',
    icon: 'briefcase',
    rolesCount: 120,
    growth: '+12%',
    color: 'violet',
  },
  {
    id: 'logistics-management',
    name: 'Logistics Management',
    icon: 'truck',
    rolesCount: 55,
    growth: '+9%',
    color: 'teal',
  },
  {
    id: 'industrial-engineering',
    name: 'Industrial Engineering',
    icon: 'code',
    rolesCount: 32,
    growth: '+14%',
    color: 'amber',
  },
];

const STARBUCKS_CAREER_FAMILIES: CareerFamily[] = [
  {
    id: 'retail-operations',
    name: 'Retail Operations',
    icon: 'storefront',
    rolesCount: 140,
    growth: '+11%',
    color: 'emerald',
  },
  {
    id: 'store-leadership',
    name: 'Store Leadership',
    icon: 'users',
    rolesCount: 60,
    growth: '+10%',
    color: 'violet',
  },
  {
    id: 'supply-chain',
    name: 'Supply Chain',
    icon: 'package',
    rolesCount: 28,
    growth: '+8%',
    color: 'amber',
  },
];

const MOCK_FEATURED_ROLES: CareerRole[] = [
  {
    id: 'nurse-manager',
    title: 'Nurse Manager',
    family: 'Nursing',
    familyIcon: 'first-aid',
    level: 'Management',
    department: 'Nursing Administration',
    salaryMin: 85000,
    salaryMax: 120000,
    growthRate: 18,
    description:
      'Oversees daily operations of a nursing unit, manages staff schedules, ensures quality patient care, and handles budget and resource allocation.',
    responsibilities: [
      'Manage nursing staff schedules and assignments',
      'Monitor and improve patient care quality metrics',
      'Develop and manage unit budget',
      'Conduct staff performance reviews',
      'Ensure compliance with healthcare regulations',
      'Coordinate with physicians and other departments',
    ],
    requiredSkills: [
      { skillId: 'leadership', name: 'Leadership', proficiencyRequired: 80, isRequired: true },
      {
        skillId: 'budget-mgmt',
        name: 'Budget Management',
        proficiencyRequired: 75,
        isRequired: true,
      },
      {
        skillId: 'staff-scheduling',
        name: 'Staff Scheduling',
        proficiencyRequired: 70,
        isRequired: true,
      },
      {
        skillId: 'quality-improvement',
        name: 'Quality Improvement',
        proficiencyRequired: 75,
        isRequired: true,
      },
    ],
    preferredSkills: [
      { skillId: 'epic', name: 'Epic EMR', proficiencyRequired: 70, isRequired: false },
      {
        skillId: 'conflict-resolution',
        name: 'Conflict Resolution',
        proficiencyRequired: 70,
        isRequired: false,
      },
    ],
    requiredCredentials: ['BSN Degree', 'RN License', '5+ years clinical experience'],
    preferredCredentials: ['MSN Degree', 'Nurse Leadership Certificate', 'Lean Six Sigma'],
    typicalPath: ['registered-nurse', 'charge-nurse'],
    nextSteps: ['director-nursing', 'cno'],
    companyOpenings: 8,
    isHighDemand: true,
    matchScore: 68,
  },
  {
    id: 'charge-nurse',
    title: 'Charge Nurse',
    family: 'Nursing',
    familyIcon: 'first-aid',
    level: 'Senior',
    department: 'Various Units',
    salaryMin: 72000,
    salaryMax: 95000,
    growthRate: 16,
    description:
      'Supervises nursing staff during a shift, coordinates patient assignments, handles immediate staffing issues.',
    responsibilities: [
      'Oversee shift operations and staff assignments',
      'Handle patient flow and bed management',
      'Coordinate with physicians and ancillary staff',
      'Respond to clinical emergencies',
      'Mentor and support nursing staff',
    ],
    requiredSkills: [
      { skillId: 'patient-care', name: 'Patient Care', proficiencyRequired: 85, isRequired: true },
      {
        skillId: 'team-leadership',
        name: 'Team Leadership',
        proficiencyRequired: 70,
        isRequired: true,
      },
      {
        skillId: 'clinical-protocols',
        name: 'Clinical Protocols',
        proficiencyRequired: 80,
        isRequired: true,
      },
    ],
    preferredSkills: [
      {
        skillId: 'crisis-management',
        name: 'Crisis Management',
        proficiencyRequired: 70,
        isRequired: false,
      },
    ],
    requiredCredentials: ['BSN Degree', 'RN License', '3+ years clinical experience'],
    preferredCredentials: ['Charge Nurse Training', 'ACLS Certification'],
    typicalPath: ['registered-nurse'],
    nextSteps: ['nurse-manager', 'clinical-educator'],
    companyOpenings: 15,
    isHighDemand: true,
    matchScore: 95,
  },
  {
    id: 'clinical-informatics-specialist',
    title: 'Clinical Informatics Specialist',
    family: 'Health Informatics',
    familyIcon: 'code',
    level: 'Mid',
    department: 'Health Information Technology',
    salaryMin: 75000,
    salaryMax: 110000,
    growthRate: 32,
    description:
      'Bridges the gap between clinical practice and technology by optimizing EMR workflows.',
    responsibilities: [
      'Design and optimize clinical workflows in Epic',
      'Train clinical staff on EMR systems',
      'Analyze healthcare data for quality improvement',
      'Support system implementations and upgrades',
      'Serve as liaison between clinical and IT teams',
    ],
    requiredSkills: [
      { skillId: 'epic', name: 'Epic EMR', proficiencyRequired: 85, isRequired: true },
      {
        skillId: 'data-analytics',
        name: 'Data Analytics',
        proficiencyRequired: 70,
        isRequired: true,
      },
      {
        skillId: 'clinical-workflows',
        name: 'Clinical Workflows',
        proficiencyRequired: 75,
        isRequired: true,
      },
    ],
    preferredSkills: [
      { skillId: 'sql', name: 'SQL', proficiencyRequired: 60, isRequired: false },
      { skillId: 'training', name: 'Training', proficiencyRequired: 70, isRequired: false },
    ],
    requiredCredentials: ['Clinical background (RN, RT, etc.)', 'Epic Certification'],
    preferredCredentials: ['Health Informatics Certificate', 'Masters in Health Informatics'],
    typicalPath: ['registered-nurse', 'unit-secretary'],
    nextSteps: ['informatics-manager', 'cmio'],
    companyOpenings: 5,
    isHighDemand: true,
    matchScore: 78,
  },
];

const UPS_CAREER_ROLES: CareerRole[] = [
  {
    id: 'package-handler',
    title: 'Package Handler',
    family: 'Operations',
    familyIcon: 'briefcase',
    level: 'Entry',
    department: 'Operations',
    salaryMin: 38000,
    salaryMax: 52000,
    growthRate: 8,
    description:
      'Handles package sorting, loading, and unloading to keep the hub moving efficiently.',
    responsibilities: [
      'Sort and scan packages for outbound routes',
      'Load and unload trailers safely',
      'Follow safety and compliance procedures',
      'Coordinate with shift supervisors on priorities',
    ],
    requiredSkills: [
      { skillId: 'safety', name: 'Safety Compliance', proficiencyRequired: 70, isRequired: true },
      { skillId: 'teamwork', name: 'Teamwork', proficiencyRequired: 65, isRequired: true },
    ],
    preferredSkills: [
      {
        skillId: 'logistics',
        name: 'Logistics Basics',
        proficiencyRequired: 60,
        isRequired: false,
      },
    ],
    requiredCredentials: ['HS Diploma/GED'],
    preferredCredentials: ['Forklift Certification'],
    typicalPath: [],
    nextSteps: ['hub-supervisor'],
    companyOpenings: 120,
    isHighDemand: true,
    matchScore: 55,
  },
  {
    id: 'hub-supervisor',
    title: 'Hub Supervisor',
    family: 'Operations',
    familyIcon: 'briefcase',
    level: 'Management',
    department: 'Hub Operations',
    salaryMin: 62000,
    salaryMax: 84000,
    growthRate: 12,
    description: 'Leads shift operations, manages performance, and ensures on-time departures.',
    responsibilities: [
      'Supervise package handlers and shift leads',
      'Monitor throughput, safety, and quality metrics',
      'Coordinate staffing and shift schedules',
      'Coach team members and manage performance',
    ],
    requiredSkills: [
      { skillId: 'leadership', name: 'Leadership', proficiencyRequired: 75, isRequired: true },
      {
        skillId: 'operations',
        name: 'Operations Management',
        proficiencyRequired: 70,
        isRequired: true,
      },
      {
        skillId: 'scheduling',
        name: 'Workforce Scheduling',
        proficiencyRequired: 65,
        isRequired: true,
      },
    ],
    preferredSkills: [
      { skillId: 'lean', name: 'Lean Process', proficiencyRequired: 60, isRequired: false },
    ],
    requiredCredentials: ['2+ years logistics experience'],
    preferredCredentials: ['Supervisor Leadership Certificate'],
    typicalPath: ['package-handler'],
    nextSteps: ['operations-manager'],
    companyOpenings: 28,
    isHighDemand: true,
    matchScore: 62,
  },
  {
    id: 'operations-manager',
    title: 'Operations Manager',
    family: 'Logistics Management',
    familyIcon: 'truck',
    level: 'Senior',
    department: 'Operations',
    salaryMin: 90000,
    salaryMax: 120000,
    growthRate: 9,
    description: 'Owns regional operations performance, cost optimization, and people development.',
    responsibilities: [
      'Lead multi-shift operational strategy',
      'Drive safety, service, and cost KPIs',
      'Partner with HR on talent development',
      'Implement continuous improvement initiatives',
    ],
    requiredSkills: [
      {
        skillId: 'strategy',
        name: 'Operational Strategy',
        proficiencyRequired: 80,
        isRequired: true,
      },
      {
        skillId: 'analytics',
        name: 'Operational Analytics',
        proficiencyRequired: 75,
        isRequired: true,
      },
    ],
    preferredSkills: [
      { skillId: 'six-sigma', name: 'Six Sigma', proficiencyRequired: 70, isRequired: false },
    ],
    requiredCredentials: ['5+ years logistics leadership'],
    preferredCredentials: ['Lean Six Sigma Green Belt'],
    typicalPath: ['hub-supervisor'],
    nextSteps: [],
    companyOpenings: 10,
    isHighDemand: true,
    matchScore: 48,
  },
];

const STARBUCKS_CAREER_ROLES: CareerRole[] = [
  {
    id: 'barista',
    title: 'Barista',
    family: 'Retail Operations',
    familyIcon: 'storefront',
    level: 'Entry',
    department: 'Store Operations',
    salaryMin: 32000,
    salaryMax: 42000,
    growthRate: 10,
    description: 'Delivers great customer experiences and crafts beverages to quality standards.',
    responsibilities: [
      'Prepare beverages to brand standards',
      'Deliver fast, friendly customer service',
      'Maintain cleanliness and food safety',
      'Support store operations and restocking',
    ],
    requiredSkills: [
      { skillId: 'customer', name: 'Customer Service', proficiencyRequired: 70, isRequired: true },
      { skillId: 'quality', name: 'Quality Focus', proficiencyRequired: 65, isRequired: true },
    ],
    preferredSkills: [
      { skillId: 'teamwork', name: 'Teamwork', proficiencyRequired: 60, isRequired: false },
    ],
    requiredCredentials: ['HS Diploma/GED'],
    preferredCredentials: ['Food Safety Certification'],
    typicalPath: [],
    nextSteps: ['shift-supervisor'],
    companyOpenings: 95,
    isHighDemand: true,
    matchScore: 58,
  },
  {
    id: 'shift-supervisor',
    title: 'Shift Supervisor',
    family: 'Store Leadership',
    familyIcon: 'users',
    level: 'Mid',
    department: 'Store Operations',
    salaryMin: 42000,
    salaryMax: 56000,
    growthRate: 11,
    description: 'Leads daily shifts, coaches partners, and ensures customer experience standards.',
    responsibilities: [
      'Lead shift execution and staffing',
      'Coach baristas and uphold standards',
      'Handle inventory checks and cash management',
      'Resolve customer issues in the moment',
    ],
    requiredSkills: [
      { skillId: 'leadership', name: 'Leadership', proficiencyRequired: 70, isRequired: true },
      {
        skillId: 'operations',
        name: 'Store Operations',
        proficiencyRequired: 70,
        isRequired: true,
      },
    ],
    preferredSkills: [
      { skillId: 'training', name: 'Partner Training', proficiencyRequired: 65, isRequired: false },
    ],
    requiredCredentials: ['1+ year retail experience'],
    preferredCredentials: ['Shift Supervisor Training'],
    typicalPath: ['barista'],
    nextSteps: ['store-manager'],
    companyOpenings: 40,
    isHighDemand: true,
    matchScore: 64,
  },
  {
    id: 'store-manager',
    title: 'Store Manager',
    family: 'Store Leadership',
    familyIcon: 'users',
    level: 'Management',
    department: 'Store Operations',
    salaryMin: 65000,
    salaryMax: 85000,
    growthRate: 9,
    description: 'Owns store performance, partner development, and community engagement.',
    responsibilities: [
      'Drive sales and customer connection metrics',
      'Hire, coach, and develop store leaders',
      'Manage labor and operational budgets',
      'Build local community partnerships',
    ],
    requiredSkills: [
      { skillId: 'business', name: 'Business Acumen', proficiencyRequired: 75, isRequired: true },
      { skillId: 'leadership', name: 'Leadership', proficiencyRequired: 80, isRequired: true },
    ],
    preferredSkills: [
      {
        skillId: 'analytics',
        name: 'Retail Analytics',
        proficiencyRequired: 70,
        isRequired: false,
      },
    ],
    requiredCredentials: ['3+ years retail leadership'],
    preferredCredentials: ['Store Manager Development Program'],
    typicalPath: ['shift-supervisor'],
    nextSteps: [],
    companyOpenings: 18,
    isHighDemand: true,
    matchScore: 58,
  },
];

export const MOCK_CAREER_ROLES: CareerRole[] = MOCK_FEATURED_ROLES;

const MOCK_SKILL_GAPS: SkillGap[] = [
  {
    id: 'gap-budget',
    skillId: 'budget-mgmt',
    name: 'Budget Management',
    importance: 'Critical',
    currentLevel: 0,
    requiredLevel: 80,
    targetRole: 'Nurse Manager',
    description:
      'Understanding healthcare financial management, cost analysis, resource allocation.',
    whyNeeded:
      'Nurse Managers are responsible for unit budgets ranging from $500K to $2M annually.',
    timeToClose: '3-4 months',
    learningPaths: [
      {
        id: 'path-hfm',
        title: 'Healthcare Financial Management Certificate',
        provider: 'ACHE',
        type: 'Certificate',
        duration: '8 weeks',
        cost: '$1,200',
        costValue: 1200,
        taEligible: true,
        format: 'Online',
        matchScore: 98,
        description:
          'Comprehensive program covering healthcare budgeting, cost analysis, and financial reporting.',
        skillsGained: [
          'Budget Planning',
          'Cost Analysis',
          'Financial Reporting',
          'Resource Allocation',
        ],
        reviews: { rating: 4.8, count: 342 },
      },
      {
        id: 'path-budget-basics',
        title: 'Nursing Unit Budget Basics',
        provider: 'Hospital Internal',
        type: 'Workshop',
        duration: '2 days',
        cost: 'Free',
        costValue: 0,
        taEligible: false,
        format: 'In-person',
        matchScore: 85,
        description: 'Internal workshop focused on practical budget management for nursing units.',
        skillsGained: ['Budget Basics', 'Expense Tracking'],
        reviews: { rating: 4.5, count: 89 },
      },
    ],
  },
  {
    id: 'gap-scheduling',
    skillId: 'staff-scheduling',
    name: 'Staff Scheduling',
    importance: 'Important',
    currentLevel: 20,
    requiredLevel: 75,
    targetRole: 'Nurse Manager',
    description: 'Creating efficient nursing schedules, managing shift coverage.',
    whyNeeded:
      'Proper staff scheduling directly impacts patient care quality and nurse satisfaction.',
    timeToClose: '2-3 months',
    learningPaths: [
      {
        id: 'path-workforce',
        title: 'Healthcare Workforce Management',
        provider: 'AONL',
        type: 'Certificate',
        duration: '6 weeks',
        cost: '$800',
        costValue: 800,
        taEligible: true,
        format: 'Hybrid',
        matchScore: 95,
        description: 'Covers scheduling software, staffing ratios, predictive scheduling.',
        skillsGained: [
          'Scheduling',
          'Staffing Ratios',
          'Workforce Planning',
          'Coverage Management',
        ],
        reviews: { rating: 4.7, count: 215 },
      },
    ],
  },
  {
    id: 'gap-qi',
    skillId: 'quality-improvement',
    name: 'Quality Improvement',
    importance: 'Important',
    currentLevel: 35,
    requiredLevel: 85,
    targetRole: 'Nurse Manager',
    description: 'Leading quality improvement initiatives, understanding healthcare metrics.',
    whyNeeded: 'Nurse Managers are expected to lead QI projects that improve patient outcomes.',
    timeToClose: '4-5 months',
    learningPaths: [
      {
        id: 'path-lss',
        title: 'Lean Six Sigma Yellow Belt for Healthcare',
        provider: 'IHI',
        type: 'Certificate',
        duration: '8 weeks',
        cost: '$1,500',
        costValue: 1500,
        taEligible: true,
        format: 'Online',
        matchScore: 97,
        description: 'Industry-standard quality improvement methodology.',
        skillsGained: [
          'Process Improvement',
          'Data Analysis',
          'Root Cause Analysis',
          'PDSA Cycles',
        ],
        reviews: { rating: 4.9, count: 890 },
      },
    ],
  },
];

const UPS_SKILL_GAPS: SkillGap[] = [
  {
    id: 'ups-gap-leadership',
    skillId: 'leadership',
    name: 'Leadership',
    importance: 'Critical',
    currentLevel: 40,
    requiredLevel: 75,
    targetRole: 'Hub Supervisor',
    description: 'Leading teams, coaching performance, and building accountability.',
    whyNeeded: 'Supervisors are responsible for shift outcomes and team engagement.',
    timeToClose: '3-4 months',
    learningPaths: [
      {
        id: 'ups-path-leadership',
        title: 'Supervisor Leadership Program',
        provider: 'UPS Learning',
        type: 'Training',
        duration: '10 weeks',
        cost: '$1,500',
        costValue: 1500,
        taEligible: true,
        format: 'Hybrid',
        matchScore: 92,
        description: 'Coaching, feedback, and operational leadership fundamentals.',
        skillsGained: ['Leadership', 'Coaching', 'Team Development'],
        reviews: { rating: 4.6, count: 320 },
      },
    ],
  },
  {
    id: 'ups-gap-scheduling',
    skillId: 'scheduling',
    name: 'Workforce Scheduling',
    importance: 'Important',
    currentLevel: 30,
    requiredLevel: 70,
    targetRole: 'Hub Supervisor',
    description: 'Building efficient shift plans to meet volume targets.',
    whyNeeded: 'Accurate schedules improve service levels and reduce overtime.',
    timeToClose: '2-3 months',
    learningPaths: [
      {
        id: 'ups-path-scheduling',
        title: 'Workforce Scheduling & Labor Planning',
        provider: 'ASU Online',
        type: 'Course',
        duration: '6 weeks',
        cost: '$900',
        costValue: 900,
        taEligible: true,
        format: 'Online',
        matchScore: 86,
        description: 'Forecasting volume, staffing ratios, and shift optimization.',
        skillsGained: ['Scheduling', 'Forecasting', 'Staffing'],
        reviews: { rating: 4.5, count: 210 },
      },
    ],
  },
  {
    id: 'ups-gap-operations',
    skillId: 'operations',
    name: 'Operations Management',
    importance: 'Important',
    currentLevel: 45,
    requiredLevel: 75,
    targetRole: 'Hub Supervisor',
    description: 'Managing throughput, safety, and service KPIs.',
    whyNeeded: 'Supervisors drive daily performance across multiple teams.',
    timeToClose: '4-5 months',
    learningPaths: [
      {
        id: 'ups-path-ops',
        title: 'Operations Excellence',
        provider: 'Coursera',
        type: 'Course',
        duration: '8 weeks',
        cost: '$750',
        costValue: 750,
        taEligible: true,
        format: 'Online',
        matchScore: 82,
        description: 'KPIs, service levels, and continuous improvement basics.',
        skillsGained: ['Operations', 'Analytics', 'Continuous Improvement'],
        reviews: { rating: 4.4, count: 180 },
      },
    ],
  },
];

const STARBUCKS_SKILL_GAPS: SkillGap[] = [
  {
    id: 'sbux-gap-leadership',
    skillId: 'leadership',
    name: 'Leadership',
    importance: 'Critical',
    currentLevel: 45,
    requiredLevel: 80,
    targetRole: 'Store Manager',
    description: 'Leading partners, coaching performance, and building culture.',
    whyNeeded: 'Store Managers are accountable for partner development and store results.',
    timeToClose: '3-4 months',
    learningPaths: [
      {
        id: 'sbux-path-leadership',
        title: 'Store Manager Development',
        provider: 'Starbucks Learning',
        type: 'Training',
        duration: '12 weeks',
        cost: '$1,800',
        costValue: 1800,
        taEligible: true,
        format: 'Hybrid',
        matchScore: 94,
        description: 'Leadership, coaching, and operational excellence.',
        skillsGained: ['Leadership', 'Coaching', 'Store Operations'],
        reviews: { rating: 4.7, count: 280 },
      },
    ],
  },
  {
    id: 'sbux-gap-operations',
    skillId: 'operations',
    name: 'Store Operations',
    importance: 'Important',
    currentLevel: 50,
    requiredLevel: 78,
    targetRole: 'Store Manager',
    description: 'Inventory, scheduling, and daily operations management.',
    whyNeeded: 'Strong operations drive customer experience and profitability.',
    timeToClose: '2-3 months',
    learningPaths: [
      {
        id: 'sbux-path-ops',
        title: 'Retail Operations Certificate',
        provider: 'ASU Online',
        type: 'Certificate',
        duration: '8 weeks',
        cost: '$1,100',
        costValue: 1100,
        taEligible: true,
        format: 'Online',
        matchScore: 87,
        description: 'Operations, scheduling, inventory, and service standards.',
        skillsGained: ['Operations', 'Scheduling', 'Inventory'],
        reviews: { rating: 4.6, count: 240 },
      },
    ],
  },
  {
    id: 'sbux-gap-finance',
    skillId: 'business',
    name: 'Business Acumen',
    importance: 'Important',
    currentLevel: 35,
    requiredLevel: 75,
    targetRole: 'Store Manager',
    description: 'Understanding store P&L, labor budgets, and performance metrics.',
    whyNeeded: 'Store Managers balance labor, inventory, and sales targets.',
    timeToClose: '4-5 months',
    learningPaths: [
      {
        id: 'sbux-path-finance',
        title: 'Retail Financial Management',
        provider: 'Coursera',
        type: 'Course',
        duration: '6 weeks',
        cost: '$600',
        costValue: 600,
        taEligible: true,
        format: 'Online',
        matchScore: 80,
        description: 'Core financial concepts for retail leaders.',
        skillsGained: ['Budgeting', 'Financial Analysis', 'KPIs'],
        reviews: { rating: 4.5, count: 190 },
      },
    ],
  },
];

const MOCK_CAREER_PLAN: CareerPlan = {
  id: 'plan-001',
  employeeId: 'emp-001',
  targetRole: 'nurse-manager',
  targetRoleTitle: 'Nurse Manager',
  targetDate: '2026-12-01',
  progress: 68,
  estimatedTimeMonths: 18,
  status: 'active',
  createdAt: '2024-01-15',
  milestones: [
    {
      id: 'ms-1',
      title: 'Complete BSN Degree',
      type: 'certification',
      status: 'completed',
      completedDate: '2024-08-15',
      description: 'RN-to-BSN program via Tuition Assistance',
      taEligible: true,
      costCovered: 12000,
      provider: 'University Partners Network',
    },
    {
      id: 'ms-2',
      title: 'Nurse Leadership Certificate',
      type: 'course',
      status: 'in-progress',
      progress: 65,
      targetDate: '2025-02-15',
      description: 'ANCC Certificate - currently in Module 4',
      taEligible: true,
      costCovered: 1850,
      provider: 'ANCC',
    },
    {
      id: 'ms-3',
      title: 'Epic EMR Super User Training',
      type: 'course',
      status: 'in-progress',
      progress: 40,
      targetDate: '2025-03-01',
      description: 'Internal certification for EMR workflow optimization',
      taEligible: false,
      provider: 'Hospital Internal',
    },
    {
      id: 'ms-4',
      title: 'Complete Charge Nurse Training',
      type: 'certification',
      status: 'planned',
      targetDate: '2025-06-01',
      description: 'Internal program - required for management track',
    },
    {
      id: 'ms-5',
      title: 'Shadow Nurse Manager',
      type: 'experience',
      status: 'planned',
      targetDate: '2025-09-01',
      description: 'Job shadow with current unit manager for 2 weeks',
    },
    {
      id: 'ms-6',
      title: 'Apply for Nurse Manager Role',
      type: 'milestone',
      status: 'planned',
      targetDate: '2026-12-01',
      description: 'Target open Nurse Manager position in Med-Surg or ICU',
    },
  ],
};

const UPS_CAREER_PLAN: CareerPlan = {
  id: 'plan-ups-001',
  employeeId: 'emp-ups-001',
  targetRole: 'hub-supervisor',
  targetRoleTitle: 'Hub Supervisor',
  targetDate: '2026-06-01',
  progress: 42,
  estimatedTimeMonths: 16,
  status: 'active',
  createdAt: '2024-03-10',
  milestones: [
    {
      id: 'ups-ms-1',
      title: 'Complete Safety & Compliance Training',
      type: 'certification',
      status: 'completed',
      completedDate: '2024-05-12',
      description: 'Required OSHA and hub safety certification',
      taEligible: false,
    },
    {
      id: 'ups-ms-2',
      title: 'Supervisor Leadership Program',
      type: 'course',
      status: 'in-progress',
      progress: 55,
      targetDate: '2025-02-10',
      description: 'Internal leadership program focused on coaching and operations',
      taEligible: true,
      costCovered: 1500,
      provider: 'UPS Learning',
    },
    {
      id: 'ups-ms-3',
      title: 'Workforce Scheduling Workshop',
      type: 'course',
      status: 'planned',
      targetDate: '2025-05-15',
      description: 'Shift planning and staffing optimization',
      taEligible: true,
      provider: 'ASU Online',
    },
    {
      id: 'ups-ms-4',
      title: 'Lead Peak Season Shift',
      type: 'experience',
      status: 'planned',
      targetDate: '2025-11-15',
      description: 'Hands-on leadership during peak season operations',
    },
  ],
};

const STARBUCKS_CAREER_PLAN: CareerPlan = {
  id: 'plan-sbux-001',
  employeeId: 'emp-sbux-001',
  targetRole: 'store-manager',
  targetRoleTitle: 'Store Manager',
  targetDate: '2027-01-15',
  progress: 35,
  estimatedTimeMonths: 20,
  status: 'active',
  createdAt: '2024-04-01',
  milestones: [
    {
      id: 'sbux-ms-1',
      title: 'Coffee Master Certification',
      type: 'certification',
      status: 'completed',
      completedDate: '2024-08-20',
      description: 'Advanced coffee knowledge and quality standards',
      taEligible: false,
    },
    {
      id: 'sbux-ms-2',
      title: 'Store Manager Development',
      type: 'course',
      status: 'in-progress',
      progress: 40,
      targetDate: '2025-03-15',
      description: 'Leadership, operations, and financial management training',
      taEligible: true,
      costCovered: 1800,
      provider: 'Starbucks Learning',
    },
    {
      id: 'sbux-ms-3',
      title: 'Retail Operations Certificate',
      type: 'course',
      status: 'planned',
      targetDate: '2025-09-01',
      description: 'Customer experience, scheduling, and inventory excellence',
      taEligible: true,
      provider: 'ASU Online',
    },
    {
      id: 'sbux-ms-4',
      title: 'Lead New Store Opening',
      type: 'experience',
      status: 'planned',
      targetDate: '2026-05-01',
      description: 'Operational leadership experience for a new store launch',
    },
  ],
};

const MOCK_RECOMMENDATIONS: Recommendation[] = [
  {
    id: 'rec-1',
    title: 'Nurse Leadership Certificate',
    provider: 'American Nurses Credentialing Center (ANCC)',
    type: 'Certificate',
    duration: '4 months',
    cost: '$1,850',
    taEligible: true,
    matchScore: 98,
    skillsGained: ['Leadership', 'Team Management', 'Quality Improvement', 'Conflict Resolution'],
    salaryImpact: '+$12,000',
    completionRate: 88,
    reason: 'Perfect match for your Nurse Manager goal. Covers leadership skills gap.',
    isTopPick: true,
    badge: 'Best Match',
  },
  {
    id: 'rec-2',
    title: 'Epic Clinical Documentation Certification',
    provider: 'Epic Systems',
    type: 'Certification',
    duration: '6 weeks',
    cost: '$450',
    taEligible: true,
    matchScore: 95,
    skillsGained: ['Epic EMR', 'Clinical Documentation', 'Workflow Optimization'],
    salaryImpact: '+$8,000',
    completionRate: 92,
    reason: 'High priority skill at your hospital. Opens door to Informatics roles.',
    badge: 'Hospital Priority',
    isTopPick: false,
  },
  {
    id: 'rec-3',
    title: 'Healthcare Financial Management Certificate',
    provider: 'ACHE',
    type: 'Certificate',
    duration: '8 weeks',
    cost: '$1,200',
    taEligible: true,
    matchScore: 92,
    skillsGained: ['Budget Planning', 'Cost Analysis', 'Financial Reporting'],
    salaryImpact: '+$10,000',
    completionRate: 85,
    reason: 'Addresses your critical Budget Management gap.',
    badge: 'Skill Gap',
    isTopPick: false,
  },
  {
    id: 'rec-4',
    title: 'Charge Nurse Training Program',
    provider: 'Hospital Internal Learning',
    type: 'Training',
    duration: '8 weeks',
    cost: 'Free',
    taEligible: false,
    matchScore: 90,
    skillsGained: ['Shift Management', 'Staffing', 'Incident Response', 'Delegation'],
    salaryImpact: '+$6,000',
    completionRate: 95,
    reason: 'Next step on your leadership path. Internal program, no cost to you.',
    badge: 'Quick Win',
    isTopPick: false,
  },
  {
    id: 'rec-5',
    title: 'Lean Six Sigma Yellow Belt for Healthcare',
    provider: 'IHI',
    type: 'Certificate',
    duration: '8 weeks',
    cost: '$1,500',
    taEligible: true,
    matchScore: 88,
    skillsGained: ['Process Improvement', 'Data Analysis', 'Root Cause Analysis'],
    salaryImpact: '+$8,000',
    completionRate: 87,
    reason: 'Industry-standard QI methodology highly valued for management.',
    isTopPick: false,
  },
];

const UPS_RECOMMENDATIONS: Recommendation[] = [
  {
    id: 'ups-rec-1',
    title: 'Supervisor Leadership Program',
    provider: 'UPS Learning',
    type: 'Training',
    duration: '10 weeks',
    cost: '$1,500',
    taEligible: true,
    matchScore: 92,
    skillsGained: ['Leadership', 'Coaching', 'Operations'],
    salaryImpact: '+8%',
    completionRate: 88,
    reason: 'Directly aligned with Hub Supervisor competencies.',
    badge: 'Top Match',
    isTopPick: true,
  },
  {
    id: 'ups-rec-2',
    title: 'Workforce Scheduling & Labor Planning',
    provider: 'ASU Online',
    type: 'Course',
    duration: '6 weeks',
    cost: '$900',
    taEligible: true,
    matchScore: 86,
    skillsGained: ['Scheduling', 'Forecasting', 'Staffing Ratios'],
    salaryImpact: '+4%',
    completionRate: 82,
    reason: 'Improves shift planning efficiency and service levels.',
    isTopPick: false,
  },
  {
    id: 'ups-rec-3',
    title: 'Lean Six Sigma Yellow Belt',
    provider: 'ASQ',
    type: 'Certification',
    duration: '8 weeks',
    cost: '$1,200',
    taEligible: true,
    matchScore: 80,
    skillsGained: ['Process Improvement', 'Root Cause Analysis'],
    salaryImpact: '+3%',
    completionRate: 76,
    reason: 'Supports continuous improvement goals.',
    isTopPick: false,
  },
];

const STARBUCKS_RECOMMENDATIONS: Recommendation[] = [
  {
    id: 'sbux-rec-1',
    title: 'Store Manager Development',
    provider: 'Starbucks Learning',
    type: 'Training',
    duration: '12 weeks',
    cost: '$1,800',
    taEligible: true,
    matchScore: 94,
    skillsGained: ['Leadership', 'Financial Management', 'Coaching'],
    salaryImpact: '+10%',
    completionRate: 90,
    reason: 'Core pathway for Store Manager readiness.',
    badge: 'Top Match',
    isTopPick: true,
  },
  {
    id: 'sbux-rec-2',
    title: 'Retail Operations Certificate',
    provider: 'ASU Online',
    type: 'Certificate',
    duration: '8 weeks',
    cost: '$1,100',
    taEligible: true,
    matchScore: 87,
    skillsGained: ['Store Operations', 'Inventory', 'Scheduling'],
    salaryImpact: '+5%',
    completionRate: 84,
    reason: 'Strengthens operational excellence and cost control.',
    isTopPick: false,
  },
  {
    id: 'sbux-rec-3',
    title: 'Customer Experience Analytics',
    provider: 'Coursera',
    type: 'Course',
    duration: '6 weeks',
    cost: '$450',
    taEligible: true,
    matchScore: 78,
    skillsGained: ['Customer Experience', 'Data Insights'],
    salaryImpact: '+3%',
    completionRate: 79,
    reason: 'Improves connection scores and retention.',
    isTopPick: false,
  },
];

const MOCK_SUCCESS_STORIES: SuccessStory[] = [
  {
    id: 'story-1',
    employeeName: 'Maria R.',
    initials: 'MR',
    avatarColor: 'tio-story-avatar--pink-rose',
    fromRole: 'CNA (Certified Nursing Assistant)',
    toRole: 'Registered Nurse (RN)',
    department: 'Medical-Surgical Unit',
    timeframe: '3 years',
    salaryIncrease: '+85%',
    quote:
      "I started as a CNA on the night shift. Career Pathways showed me the RN bridge program was covered by TA. Three years later, I'm an RN in the same unit where I started!",
    skillsGained: [
      'Patient Assessment',
      'Medication Administration',
      'Care Planning',
      'IV Therapy',
    ],
    programsCompleted: ['RN Bridge Program', 'NCLEX Prep Course'],
    featured: true,
    likes: 312,
  },
  {
    id: 'story-2',
    employeeName: 'James T.',
    initials: 'JT',
    avatarColor: 'tio-story-avatar--blue-indigo',
    fromRole: 'Unit Secretary',
    toRole: 'Clinical Informatics Analyst',
    department: 'Health Information Technology',
    timeframe: '2 years',
    salaryIncrease: '+68%',
    quote:
      'I was good at our EMR system and helped train others. The AI Advisor suggested Clinical Informatics - I had no idea it was a career!',
    skillsGained: ['Epic EMR', 'Workflow Analysis', 'Training', 'SQL'],
    programsCompleted: ['Health Informatics Certificate', 'Epic Certification'],
    featured: true,
    likes: 245,
  },
  {
    id: 'story-3',
    employeeName: 'Sarah L.',
    initials: 'SL',
    avatarColor: 'tio-story-avatar--violet-purple',
    fromRole: 'Staff RN (ICU)',
    toRole: 'Nurse Manager',
    department: 'Intensive Care Unit',
    timeframe: '4 years',
    salaryIncrease: '+45%',
    quote:
      'Career Pathways mapped out exactly what I needed: BSN completion, leadership training, and operational experience.',
    skillsGained: [
      'Budget Management',
      'Staff Development',
      'Quality Improvement',
      'Strategic Planning',
    ],
    programsCompleted: [
      'BSN Completion',
      'Nurse Leadership Certificate',
      'Healthcare Management Certificate',
    ],
    featured: true,
    likes: 423,
  },
];

const UPS_SUCCESS_STORIES: SuccessStory[] = [
  {
    id: 'ups-story-1',
    employeeName: 'Carlos Johnson',
    initials: 'CJ',
    avatarColor: 'tio-story-avatar--blue-indigo',
    fromRole: 'Package Handler',
    toRole: 'Hub Supervisor',
    department: 'Operations',
    timeframe: '18 months',
    salaryIncrease: '+15%',
    quote:
      'The leadership program and scheduling course helped me lead a peak season shift with confidence.',
    skillsGained: ['Leadership', 'Operations', 'Scheduling'],
    programsCompleted: ['Supervisor Leadership Program', 'Workforce Scheduling'],
    featured: true,
    likes: 312,
  },
  {
    id: 'ups-story-2',
    employeeName: 'Samantha Lee',
    initials: 'SL',
    avatarColor: 'tio-story-avatar--teal-cyan',
    fromRole: 'Hub Supervisor',
    toRole: 'Operations Manager',
    department: 'Logistics Management',
    timeframe: '2 years',
    salaryIncrease: '+20%',
    quote: 'Six Sigma training gave me the tools to drive real operational improvements.',
    skillsGained: ['Process Improvement', 'Analytics'],
    programsCompleted: ['Lean Six Sigma Yellow Belt'],
    featured: false,
    likes: 188,
  },
];

const STARBUCKS_SUCCESS_STORIES: SuccessStory[] = [
  {
    id: 'sbux-story-1',
    employeeName: 'Alex Kim',
    initials: 'AK',
    avatarColor: 'tio-story-avatar--emerald-green',
    fromRole: 'Barista',
    toRole: 'Store Manager',
    department: 'Store Operations',
    timeframe: '2.5 years',
    salaryIncrease: '+18%',
    quote: 'The store manager development track gave me a clear roadmap to grow my career.',
    skillsGained: ['Leadership', 'Financial Management'],
    programsCompleted: ['Store Manager Development', 'Retail Operations Certificate'],
    featured: true,
    likes: 284,
  },
  {
    id: 'sbux-story-2',
    employeeName: 'Jordan Moore',
    initials: 'JM',
    avatarColor: 'tio-story-avatar--violet-purple',
    fromRole: 'Shift Supervisor',
    toRole: 'Store Manager',
    department: 'Store Leadership',
    timeframe: '20 months',
    salaryIncrease: '+14%',
    quote: 'ASU coursework helped me sharpen my business skills while I worked full time.',
    skillsGained: ['Store Operations', 'Customer Experience'],
    programsCompleted: ['Retail Operations Certificate'],
    featured: false,
    likes: 156,
  },
];

const MOCK_SUMMARY: CareerSummary = {
  totalGaps: 3,
  criticalGaps: 1,
  avgTimeToClose: '3 months',
  totalLearningOptions: 9,
  taEligibleOptions: 6,
  maxTaCoverage: '$5,250/year',
  skillsTracked: 12,
  skillsVerified: 3,
  skillsInProgress: 2,
  skillsMastered: 3,
  coursesInProgress: 2,
  profileCompleteness: 78,
};

export const MOCK_ASSESSMENT_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'q1',
    skillId: 'leadership',
    question: 'When a team member consistently underperforms, what is your first approach?',
    options: [
      { id: 'q1-a', text: 'Document issues and escalate to HR', value: 2 },
      { id: 'q1-b', text: 'Have a private conversation to understand barriers', value: 4 },
      { id: 'q1-c', text: 'Assign them easier tasks', value: 1 },
      { id: 'q1-d', text: 'Provide additional training and clear expectations', value: 3 },
    ],
  },
  {
    id: 'q2',
    skillId: 'leadership',
    question: 'How do you typically communicate important changes to your team?',
    options: [
      { id: 'q2-a', text: 'Send an email with all details', value: 2 },
      { id: 'q2-b', text: 'Hold a team meeting with time for questions', value: 4 },
      { id: 'q2-c', text: 'Tell individual team members as you see them', value: 1 },
      { id: 'q2-d', text: 'Post on the unit bulletin board', value: 1 },
    ],
  },
  {
    id: 'q3',
    skillId: 'leadership',
    question: 'A conflict arises between two team members during a busy shift. What do you do?',
    options: [
      { id: 'q3-a', text: 'Let them work it out themselves', value: 1 },
      { id: 'q3-b', text: 'Separate them and address it after the shift', value: 3 },
      { id: 'q3-c', text: "Immediately mediate to ensure patient care isn't affected", value: 4 },
      { id: 'q3-d', text: 'Report both to HR', value: 1 },
    ],
  },
];

export const MOCK_LRAP_PROGRAMS: LrapProgram[] = [
  {
    id: 'lrap-georgetown',
    name: 'Georgetown LRAP III',
    type: 'school',
    sponsor: 'Georgetown University Law Center',
    description:
      'Covers 100% of Direct Loan payments for graduates in public interest work earning under $75,000.',
    maxAnnual: 12000,
    maxLifetime: null,
    eligibilityRequirements: [
      'Georgetown Law graduate',
      'Public interest employment',
      'Salary under $75,000',
      'Enrolled in IDR plan',
    ],
    applicationDeadline: '2026-03-15',
    applicationUrl: 'https://www.law.georgetown.edu/admissions-aid/financial-aid/lrap/',
    stackingNotes: 'Can be combined with PSLF. May reduce based on outside funds.',
    incompatibleWith: [],
    outsideFundsPolicy: 'reduces',
  },
  {
    id: 'lrap-nyu',
    name: 'NYU LRAP Plus',
    type: 'school',
    sponsor: 'NYU School of Law',
    description:
      'Income-driven assistance covering 100% of IDR payments for qualifying public interest positions.',
    maxAnnual: 15000,
    maxLifetime: null,
    eligibilityRequirements: [
      'NYU Law graduate',
      'Qualifying public interest work',
      'Income under $110,000',
    ],
    applicationDeadline: '2026-04-01',
    applicationUrl: 'https://www.law.nyu.edu/financialaid/lrap',
    stackingNotes: 'Highly integrated with PSLF tracking.',
    incompatibleWith: [],
    outsideFundsPolicy: 'reduces',
  },
  {
    id: 'lrap-florida-bar',
    name: 'Florida Bar Foundation LRAP',
    type: 'state',
    sponsor: 'The Florida Bar Foundation',
    description:
      'Loan repayment assistance for attorneys providing civil legal aid to low-income Floridians.',
    maxAnnual: 5000,
    maxLifetime: 25000,
    eligibilityRequirements: [
      'Licensed Florida attorney',
      'Employed by eligible legal aid organization',
      'Salary under $65,000',
    ],
    applicationDeadline: '2026-05-15',
    applicationUrl: 'https://thefloridabarfoundation.org/lrap',
    stackingNotes: 'Can be combined with employer contributions.',
    incompatibleWith: [],
    outsideFundsPolicy: 'no-impact',
  },
  {
    id: 'lrap-maryland-hoffman',
    name: 'Janet L. Hoffman LRAP (Maryland)',
    type: 'state',
    sponsor: 'Maryland Higher Education Commission',
    description:
      'Loan assistance for Maryland residents working in state or local government or nonprofit organizations.',
    maxAnnual: 7500,
    maxLifetime: 30000,
    eligibilityRequirements: [
      'Maryland resident',
      'State/local government or nonprofit employee',
      'Minimum 2-year service commitment',
    ],
    applicationDeadline: '2026-06-01',
    applicationUrl: 'https://mhec.maryland.gov/preparing/Pages/FinancialAid/descriptions.aspx',
    stackingNotes: 'Compatible with federal PSLF program.',
    incompatibleWith: [],
    outsideFundsPolicy: 'no-impact',
  },
  {
    id: 'lrap-doj',
    name: 'DOJ Attorney Student Loan Repayment Program',
    type: 'federal',
    sponsor: 'U.S. Department of Justice',
    description:
      'Federal agency LRAP for DOJ attorneys under 5 U.S.C. 5379, up to $10,000/year with 3-year commitment.',
    maxAnnual: 10000,
    maxLifetime: 60000,
    eligibilityRequirements: [
      'DOJ attorney employee',
      '3-year service agreement',
      'Federal student loans only',
    ],
    applicationDeadline: null,
    applicationUrl: 'https://www.justice.gov/legal-careers',
    stackingNotes: 'Counts toward PSLF qualifying payments.',
    incompatibleWith: [],
    outsideFundsPolicy: 'no-impact',
  },
  {
    id: 'lrap-nhsc',
    name: 'National Health Service Corps (NHSC)',
    type: 'federal',
    sponsor: 'Health Resources & Services Administration',
    description:
      'Up to $75,000 for 2 years of service in Health Professional Shortage Areas (HPSAs).',
    maxAnnual: 37500,
    maxLifetime: 75000,
    eligibilityRequirements: [
      'Primary care clinician',
      'Work in HPSA-designated area',
      '2-year minimum service commitment',
    ],
    applicationDeadline: '2026-02-28',
    applicationUrl: 'https://nhsc.hrsa.gov/loan-repayment',
    stackingNotes: 'Cannot combine with certain other federal programs.',
    incompatibleWith: ['lrap-nurse-corps'],
    outsideFundsPolicy: 'no-impact',
  },
];

export function getLrapDiscoveryForClient(): LrapDiscoverySummary {
  const clientId = getCurrentDemoClientId() as string;

  // Law firm scenario - show school LRAP conflict
  if (clientId === 'legal') {
    return {
      hasCompletedDiscovery: true,
      discoveryDate: '2025-12-01',
      enrolledPrograms: [
        {
          id: 'enrollment-1',
          programId: 'lrap-georgetown',
          programName: 'Georgetown LRAP III',
          programType: 'school',
          enrolledDate: '2024-08-15',
          annualBenefit: 8400,
          ytdReceived: 4200,
          lifetimeReceived: 12600,
          nextPaymentDate: '2026-02-15',
          status: 'active',
          expirationDate: null,
        },
      ],
      eligiblePrograms: [
        {
          programId: 'lrap-florida-bar',
          program: MOCK_LRAP_PROGRAMS.find((p) => p.id === 'lrap-florida-bar')!,
          status: 'likely-eligible',
          estimatedAnnualValue: 5000,
          matchedCriteria: ['Licensed attorney', 'Legal aid work'],
          missingCriteria: ['Florida residency verification'],
          conflictWarnings: [],
          nextSteps: ['Verify Florida bar status', 'Submit application by May 15'],
        },
      ],
      totalPotentialAnnualValue: 13400,
      outsideFundsConflicts: [
        {
          schoolProgram: 'Georgetown LRAP III',
          employerContribution: 5250,
          potentialReduction: 5250,
          recommendation:
            'Your Georgetown LRAP has an "outside funds" policy. Consider routing employer contribution to private bar study loan or SECURE 2.0 match instead.',
        },
      ],
      stackingOptimization: getStackingOptimizationForClient(),
    };
  }

  // Starbucks scenario - no LRAP programs identified yet
  if (clientId === 'starbucks') {
    return {
      hasCompletedDiscovery: false,
      discoveryDate: null,
      enrolledPrograms: [],
      eligiblePrograms: [],
      totalPotentialAnnualValue: 0,
      outsideFundsConflicts: [],
      stackingOptimization: null,
    };
  }

  // Healthcare/Advocate scenario - NHSC and state nursing programs
  if (clientId === 'advocate') {
    return {
      hasCompletedDiscovery: true,
      discoveryDate: '2025-10-15',
      enrolledPrograms: [],
      eligiblePrograms: [
        {
          programId: 'lrap-nhsc',
          program: MOCK_LRAP_PROGRAMS.find((p) => p.id === 'lrap-nhsc')!,
          status: 'eligible',
          estimatedAnnualValue: 37500,
          matchedCriteria: [
            'Primary care clinician',
            'Work in HPSA-designated area',
            '2-year service commitment available',
          ],
          missingCriteria: [],
          conflictWarnings: ['Cannot combine with Nurse Corps if awarded'],
          nextSteps: [
            'Submit NHSC application by Feb 28, 2026',
            'Obtain site verification from employer',
            'Complete HPSA eligibility check',
          ],
        },
        {
          programId: 'lrap-illinois-nurse',
          program: {
            id: 'lrap-illinois-nurse',
            name: 'Illinois Nurse Educator Loan Repayment',
            type: 'state',
            sponsor: 'Illinois Student Assistance Commission',
            description: 'Up to $5,000/year for nurses who commit to teaching nursing in Illinois.',
            maxAnnual: 5000,
            maxLifetime: 20000,
            eligibilityRequirements: [
              'Illinois licensed nurse',
              'Teaching at Illinois nursing program',
              '1-year service commitment per award year',
            ],
            applicationDeadline: '2026-04-15',
            applicationUrl: 'https://www.isac.org/students/during-college/loan-repayment/',
            stackingNotes: 'Compatible with NHSC and employer programs.',
            incompatibleWith: [],
            outsideFundsPolicy: 'no-impact',
          },
          status: 'likely-eligible',
          estimatedAnnualValue: 5000,
          matchedCriteria: ['Illinois licensed nurse', 'Clinical instructor role'],
          missingCriteria: ['Teaching commitment verification'],
          conflictWarnings: [],
          nextSteps: [
            'Confirm teaching responsibilities meet program requirements',
            'Submit application by April 15',
          ],
        },
      ],
      totalPotentialAnnualValue: 42500,
      outsideFundsConflicts: [],
      stackingOptimization: {
        totalAvailableAssistance: 47750,
        optimizedAllocation: [
          {
            source: 'NHSC',
            sourceType: 'federal',
            amount: 37500,
            targetDebt: 'Federal Direct Loans',
            rationale:
              'NHSC provides the largest benefit. Accept this first and build service commitment.',
          },
          {
            source: 'Employer Section 127',
            sourceType: 'employer',
            amount: 5250,
            targetDebt: 'Private Graduate Plus Loans',
            rationale: 'Use employer benefit for private loans not covered by NHSC.',
          },
          {
            source: 'Illinois Nurse Educator',
            sourceType: 'state',
            amount: 5000,
            targetDebt: 'Any remaining student loans',
            rationale: 'Stack state program on top of federal and employer benefits.',
          },
        ],
        reasoning:
          'As a healthcare professional in a shortage area, you qualify for exceptional NHSC benefits. We recommend accepting the federal program first ($75k over 2 years), supplementing with employer contributions to private loans, and adding state benefits.',
        projectedOutcome: {
          outOfPocketMonthly: 0,
          yearsToPayoff: 2.5,
          totalForgiven: 75000,
          retirementContribution: 0,
        },
      },
    };
  }

  return {
    hasCompletedDiscovery: true,
    discoveryDate: '2025-11-15',
    enrolledPrograms: [],
    eligiblePrograms: [
      {
        programId: 'lrap-maryland-hoffman',
        program: MOCK_LRAP_PROGRAMS.find((p) => p.id === 'lrap-maryland-hoffman')!,
        status: 'check-required',
        estimatedAnnualValue: 7500,
        matchedCriteria: ['Nonprofit employment'],
        missingCriteria: ['Maryland residency', 'Service commitment'],
        conflictWarnings: [],
        nextSteps: ['Verify Maryland residency', 'Review service commitment terms'],
      },
    ],
    totalPotentialAnnualValue: 7500,
    outsideFundsConflicts: [],
    stackingOptimization: null,
  };
}

export function getStackingOptimizationForClient(): StackingOptimization {
  const clientId = getCurrentDemoClientId() as string;

  if (clientId === 'legal') {
    return {
      totalAvailableAssistance: 18650,
      optimizedAllocation: [
        {
          source: 'Georgetown LRAP',
          sourceType: 'school',
          amount: 8400,
          targetDebt: 'Federal Direct Loans (IDR payment)',
          rationale: 'School covers 100% of your IDR payment. This counts toward PSLF.',
        },
        {
          source: 'Employer Section 127',
          sourceType: 'employer',
          amount: 3000,
          targetDebt: 'Private Bar Study Loan',
          rationale:
            'Not covered by Georgetown LRAP. Routing here avoids "outside funds" reduction.',
        },
        {
          source: 'Employer Section 127',
          sourceType: 'employer',
          amount: 2250,
          targetDebt: 'SECURE 2.0 401(k) Match',
          rationale:
            'Remaining benefit triggers retirement match. Builds wealth, not just debt payoff.',
        },
      ],
      reasoning:
        'Based on your Georgetown LRAP enrollment and PSLF eligibility, we recommend $0 to federal loans (school covers IDR), $3,000 to private bar loan, and $2,250 to 401(k) via SECURE 2.0.',
      projectedOutcome: {
        outOfPocketMonthly: 0,
        yearsToPayoff: 6.2,
        totalForgiven: 68000,
        retirementContribution: 4500,
      },
    };
  }

  return {
    totalAvailableAssistance: 5250,
    optimizedAllocation: [
      {
        source: 'Employer Section 127',
        sourceType: 'employer',
        amount: 2500,
        targetDebt: 'Federal Student Loans',
        rationale: 'Accelerate payoff of highest-interest federal loans.',
      },
      {
        source: 'Employer Section 127',
        sourceType: 'employer',
        amount: 2750,
        targetDebt: 'Tuition Assistance',
        rationale: 'Invest in career advancement for long-term earning potential.',
      },
    ],
    reasoning:
      'A balanced approach maximizes both debt reduction and career growth given your current situation.',
    projectedOutcome: {
      outOfPocketMonthly: 285,
      yearsToPayoff: 8.5,
      totalForgiven: 0,
      retirementContribution: 0,
    },
  };
}

export function getPslfVaultForClient(): PslfVaultSummary {
  const clientId = getCurrentDemoClientId() as string;

  const baseProgress: PslfProgress = {
    qualifyingPayments: 48,
    paymentsRemaining: 72,
    estimatedForgivenessDate: '2032-01-15',
    currentEmployerQualifies: true,
    currentEmployerName: 'Regional Medical Center',
    currentEmployerStartDate: '2022-01-15',
    repaymentPlan: 'SAVE (transitioning to RAP)',
    planQualifies: true,
    loanType: 'Direct Loans',
    loansQualify: true,
    monthlyPayment: 285,
    projectedForgivenessAmount: 68500,
    lastCertificationDate: '2025-10-15',
    nextCertificationDue: '2026-10-15',
    riskFactors: [
      {
        id: 'risk-1',
        severity: 'medium',
        title: 'Repayment Plan Transition',
        description: 'SAVE is transitioning to RAP in 2026. Your payment may increase.',
        action: 'Review new RAP payment estimate',
        // Consumed as a route NAME by pslf-vault's `<LinkTo @route=...>` — a URL
        // path here throws "There is no route named ..." and crashes the page.
        actionUrl: 'authenticated.reimagined.plan.strategy',
      },
    ],
    milestones: [
      { payments: 12, label: '1 Year', reached: true, reachedDate: '2023-01-15' },
      { payments: 24, label: '2 Years', reached: true, reachedDate: '2024-01-15' },
      { payments: 36, label: '3 Years', reached: true, reachedDate: '2025-01-15' },
      { payments: 48, label: '4 Years', reached: true, reachedDate: '2026-01-15' },
      { payments: 60, label: 'Halfway!', reached: false, reachedDate: null },
      { payments: 84, label: '7 Years', reached: false, reachedDate: null },
      { payments: 120, label: 'Forgiveness!', reached: false, reachedDate: null },
    ],
  };

  const baseDocuments: PslfVaultDocument[] = [
    {
      id: 'doc-1',
      formType: 'ecf',
      filename: 'ECF-2025-RegionalMedical.pdf',
      uploadedAt: '2025-10-20T14:30:00Z',
      certifiedDate: '2025-11-05T09:00:00Z',
      employerName: 'Regional Medical Center',
      coversPeriod: { start: '2024-10-01', end: '2025-09-30' },
      paymentsCertified: 12,
      status: 'certified',
      rejectionReason: null,
      downloadUrl: '#',
    },
    {
      id: 'doc-2',
      formType: 'ecf',
      filename: 'ECF-2024-RegionalMedical.pdf',
      uploadedAt: '2024-10-15T10:00:00Z',
      certifiedDate: '2024-11-01T09:00:00Z',
      employerName: 'Regional Medical Center',
      coversPeriod: { start: '2023-10-01', end: '2024-09-30' },
      paymentsCertified: 12,
      status: 'certified',
      rejectionReason: null,
      downloadUrl: '#',
    },
    {
      id: 'doc-3',
      formType: 'idr-application',
      filename: 'IDR-Recertification-2025.pdf',
      uploadedAt: '2025-08-01T08:00:00Z',
      certifiedDate: '2025-08-15T09:00:00Z',
      employerName: 'N/A',
      coversPeriod: { start: '2025-08-01', end: '2026-07-31' },
      paymentsCertified: 0,
      status: 'certified',
      rejectionReason: null,
      downloadUrl: '#',
    },
  ];

  if (clientId === 'legal') {
    return {
      totalDocuments: 5,
      certifiedPeriods: 4,
      pendingCertifications: 0,
      lastUpload: '2025-12-01T10:00:00Z',
      documents: [
        ...baseDocuments,
        {
          id: 'doc-4',
          formType: 'ecf',
          filename: 'ECF-2023-PublicDefender.pdf',
          uploadedAt: '2023-10-10T14:00:00Z',
          certifiedDate: '2023-10-25T09:00:00Z',
          employerName: 'Public Defender Office',
          coversPeriod: { start: '2022-10-01', end: '2023-09-30' },
          paymentsCertified: 12,
          status: 'certified',
          rejectionReason: null,
          downloadUrl: '#',
        },
      ],
      progress: {
        ...baseProgress,
        qualifyingPayments: 52,
        paymentsRemaining: 68,
        currentEmployerName: 'Public Defender Office',
        projectedForgivenessAmount: 82000,
      },
      complianceScore: 92,
      complianceGaps: ['Annual ECF due in 10 months'],
    };
  }

  return {
    totalDocuments: 3,
    certifiedPeriods: 2,
    pendingCertifications: 0,
    lastUpload: '2025-10-20T14:30:00Z',
    documents: baseDocuments,
    progress: baseProgress,
    complianceScore: 78,
    complianceGaps: [
      'Missing ECF for employment period 2022-01 to 2022-09',
      'Consider uploading employer verification letter as backup',
    ],
  };
}

export function getPslfJourneyForClient(): PslfJourney {
  const clientId = getCurrentDemoClientId() as string;
  const vault = getPslfVaultForClient();

  // Generate payment validations based on qualifying payments
  const generatePaymentValidations = (count: number, startDate: Date): PslfPaymentValidation[] => {
    const payments: PslfPaymentValidation[] = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < Math.min(count, 12); i++) {
      const paymentDate = new Date(currentDate);
      paymentDate.setMonth(paymentDate.getMonth() - i);

      payments.push({
        id: `payment-${count - i}`,
        paymentDate: paymentDate.toISOString().split('T')[0] as string,
        amount: 285,
        paymentNumber: count - i,
        status: 'qualifying',
        qualificationChecks: [
          {
            name: 'Payment Amount',
            passed: true,
            reason: 'Full payment received on time',
            category: 'amount',
          },
          {
            name: 'Payment Timing',
            passed: true,
            reason: 'Paid within billing cycle',
            category: 'timing',
          },
          {
            name: 'Repayment Plan',
            passed: true,
            reason: 'SAVE plan qualifies for PSLF',
            category: 'plan',
          },
          {
            name: 'Employer Status',
            passed: true,
            reason: '501(c)(3) nonprofit verified',
            category: 'employer',
          },
          {
            name: 'Employment Status',
            passed: true,
            reason: '40+ hours/week confirmed',
            category: 'employment',
          },
        ],
        servicerConfirmationId: `MOHELA-${Date.now()}-${i}`,
        validatedAt: paymentDate.toISOString(),
        validationSource: 'mohela_api',
      });
    }

    return payments;
  };

  // Generate achievements based on payment count
  const generateAchievements = (qualifyingPayments: number): PslfAchievement[] => {
    const milestoneConfig = [
      { type: 'started' as const, payments: 1, badge: 'Journey Begins', emoji: '🚀' },
      { type: 'first_year' as const, payments: 12, badge: 'First Year Champion', emoji: '⭐' },
      { type: 'two_years' as const, payments: 24, badge: 'Committed Servant', emoji: '🏅' },
      { type: 'four_years' as const, payments: 48, badge: 'Steadfast Hero', emoji: '💪' },
      { type: 'halfway' as const, payments: 60, badge: 'Halfway Hero', emoji: '🏆' },
      { type: 'six_years' as const, payments: 72, badge: 'Perseverance Pro', emoji: '🌟' },
      { type: 'eight_years' as const, payments: 96, badge: 'Almost There', emoji: '🎯' },
      { type: 'home_stretch' as const, payments: 108, badge: 'Home Stretch', emoji: '🏃' },
      { type: 'final_countdown' as const, payments: 114, badge: 'Final Countdown', emoji: '⏰' },
      { type: 'forgiveness' as const, payments: 120, badge: 'Freedom!', emoji: '🎉' },
    ];

    return milestoneConfig.map((config) => {
      const reached = qualifyingPayments >= config.payments;
      const achievedDate = reached
        ? new Date(Date.now() - (qualifyingPayments - config.payments) * 30 * 24 * 60 * 60 * 1000)
        : null;

      return {
        id: `achievement-${config.type}`,
        milestoneType: config.type,
        payments: config.payments,
        achievedAt: achievedDate?.toISOString() ?? null,
        badgeName: config.badge,
        badgeEmoji: config.emoji,
        message: reached
          ? `You've completed ${config.payments} qualifying payments!`
          : `${config.payments - qualifyingPayments} payments until "${config.badge}"`,
        statsAtMilestone: reached
          ? {
              totalPaid: config.payments * 285,
              forgivenessEarned: Math.round(
                (config.payments / 120) * vault.progress.projectedForgivenessAmount
              ),
              percentComplete: Math.round((config.payments / 120) * 100),
            }
          : null,
        shared: false,
      };
    });
  };

  const buildProjection = (progress: PslfProgress): PslfProjection => {
    const monthlyRate = progress.projectedForgivenessAmount / 120;
    return {
      qualifyingPaymentsToDate: progress.qualifyingPayments,
      paymentsRemaining: progress.paymentsRemaining,
      loanBalanceAtForgiveness: progress.projectedForgivenessAmount,
      projectedForgivenessAmount: progress.projectedForgivenessAmount,
      projectedForgivenessDate: progress.estimatedForgivenessDate,
      totalPaidToDate: progress.qualifyingPayments * progress.monthlyPayment,
      totalWillPay: 120 * progress.monthlyPayment,
      forgivenessValue: progress.projectedForgivenessAmount + 120 * progress.monthlyPayment,
      monthlyForgivenessRate: monthlyRate,
      yearlyForgivenessRate: monthlyRate * 12,
      earnedToDate: Math.round(
        (progress.qualifyingPayments / 120) * progress.projectedForgivenessAmount
      ),
    };
  };

  const buildAutomationStatus = (progress: PslfProgress): PslfAutomationStatus => {
    return {
      ecfStatus: 'automated',
      ecfStatusLabel: 'AUTOMATED',
      ecfDetails: 'Your employer HR auto-signs via Tuition.io',
      ecfLastSubmitted: progress.lastCertificationDate,
      ecfNextDue: progress.nextCertificationDue,
      idrStatus: 'easy_mode',
      idrStatusLabel: 'EASY MODE ON',
      idrDetails: 'Income pulled from IRS automatically for 1-click recert',
      idrPlanExpires: '2026-08-01',
      syncStatus: 'synced',
      syncStatusLabel: 'SYNCED',
      syncDetails: 'All payments verified and matching with MOHELA',
      syncLastUpdated: new Date().toISOString(),
      policyStatus: 'protected',
      policyStatusLabel: 'PROTECTED',
      policyDetails: 'Your grandfathered status is secure',
    };
  };

  const buildProtectionStatus = (): PslfProtectionStatus => {
    return {
      threatLevel: 'low',
      grandfathered: true,
      grandfatheredSince: '2022-01-15',
      protections: [
        'Enrolled before 2026: Grandfathered under current rules',
        'Direct Loans only: Not affected by FFEL restrictions',
        'Qualifying plan: SAVE/RAP plan is congressionally protected',
        'Qualifying employer: 501(c)(3) status verified',
        'Documentation: 100% of employment history certified',
      ],
      vulnerabilities: [],
      policyUpdates: [
        {
          id: 'update-1',
          date: '2026-02-01',
          title: 'HR 1234: PSLF Expansion Act introduced',
          description: 'Bill would expand PSLF to include more employer types',
          impactOnUser: 'positive',
          impactExplanation:
            'If passed, more employers would qualify - good for future job changes',
          source: 'Congress.gov',
          sourceUrl: 'https://congress.gov/bill/119/hr/1234',
        },
        {
          id: 'update-2',
          date: '2026-01-15',
          title: 'Project 2025 PSLF proposal analysis',
          description: 'Analysis of proposed changes to PSLF program',
          impactOnUser: 'none',
          impactExplanation: 'Grandfathered borrowers would be exempt under all proposals',
          source: 'ED.gov',
          sourceUrl: 'https://studentaid.gov/pslf',
        },
      ],
    };
  };

  const buildScenarios = (projection: PslfProjection): PslfScenario[] => {
    return [
      {
        type: 'leave_public_service',
        title: 'Leave Public Service',
        description: 'What happens if you take a private sector job?',
        impact: {
          forgivenessLost: projection.projectedForgivenessAmount,
          additionalCost:
            projection.projectedForgivenessAmount + projection.paymentsRemaining * 285,
          timeImpact: `Lose ${projection.qualifyingPaymentsToDate} months of progress`,
        },
        recommendation: 'do_not',
        message: `Leaving now would cost you $${projection.projectedForgivenessAmount.toLocaleString()} in forgiveness.`,
        details: [
          `You've already earned $${projection.earnedToDate.toLocaleString()} toward forgiveness`,
          `Only ${projection.paymentsRemaining} payments remaining (${Math.round(projection.paymentsRemaining / 12)} years)`,
          'Consider staying until forgiveness, then transition to private sector',
        ],
      },
      {
        type: 'refinance',
        title: 'Refinance to Private Lender',
        description: 'What happens if you refinance your federal loans?',
        impact: {
          forgivenessLost: projection.projectedForgivenessAmount,
          additionalCost: projection.projectedForgivenessAmount * 1.15, // Plus interest
          timeImpact: 'All 120 payments start over (not allowed with private loans)',
        },
        recommendation: 'do_not',
        message: '🚨 CRITICAL: Never refinance while pursuing PSLF!',
        details: [
          'Refinanced loans become private and are PERMANENTLY ineligible for PSLF',
          'You would lose ALL qualifying payment progress',
          `This would cost you $${(projection.projectedForgivenessAmount * 1.15).toLocaleString()}+`,
          'There is NO way to undo a refinance',
        ],
      },
      {
        type: 'change_employer',
        title: 'Change to Different Qualifying Employer',
        description: 'What happens if you change jobs within public service?',
        impact: {
          forgivenessLost: 0,
          additionalCost: 0,
          timeImpact: 'No impact if new employer qualifies',
        },
        recommendation: 'safe',
        message: 'Changing to another qualifying employer is safe!',
        details: [
          'Submit ECF for current employer before leaving',
          'Verify new employer qualifies (501(c)(3), government, etc.)',
          'Submit new ECF within 30 days of starting',
          'Your payment count continues uninterrupted',
        ],
      },
      {
        type: 'change_plan',
        title: 'Change Repayment Plan',
        description: 'What happens if you switch from your current IDR plan?',
        impact: {
          forgivenessLost: 0,
          additionalCost: 0,
          timeImpact: "Varies by plan - some don't qualify",
        },
        recommendation: 'caution',
        message: 'Only switch to another qualifying IDR plan',
        details: [
          '✅ SAVE, PAYE, IBR, ICR - All qualify for PSLF',
          '❌ Standard 10-year, Graduated, Extended - Do NOT qualify',
          'Switching between qualifying IDR plans is safe',
          'Consult our Strategy Finder before switching',
        ],
      },
    ];
  };

  const projection = buildProjection(vault.progress);
  const achievements = generateAchievements(vault.progress.qualifyingPayments);
  const nextAchievement = achievements.find((a) => !a.achievedAt) ?? null;

  // Legal scenario - further along
  if (clientId === 'legal') {
    const legalProgress = { ...vault.progress, qualifyingPayments: 52, paymentsRemaining: 68 };
    const legalProjection = buildProjection(legalProgress);
    const legalAchievements = generateAchievements(52);

    return {
      progress: legalProgress,
      projection: legalProjection,
      recentPayments: generatePaymentValidations(52, new Date()),
      allPaymentsCount: { qualifying: 52, pending: 0, nonQualifying: 0 },
      automationStatus: buildAutomationStatus(legalProgress),
      protectionStatus: buildProtectionStatus(),
      achievements: legalAchievements,
      nextAchievement: legalAchievements.find((a) => !a.achievedAt) ?? null,
      scenarios: buildScenarios(legalProjection),
      vault,
    };
  }

  // Healthcare/default scenario
  return {
    progress: vault.progress,
    projection,
    recentPayments: generatePaymentValidations(vault.progress.qualifyingPayments, new Date()),
    allPaymentsCount: {
      qualifying: vault.progress.qualifyingPayments,
      pending: 0,
      nonQualifying: 0,
    },
    automationStatus: buildAutomationStatus(vault.progress),
    protectionStatus: buildProtectionStatus(),
    achievements,
    nextAchievement,
    scenarios: buildScenarios(projection),
    vault,
  };
}
